import React, { useState } from 'react'
import { Button, Popover } from 'antd'
import { Building2, Compass, Lightbulb, Plus } from 'lucide-react'
import styled from 'styled-components'
import type { NodeCategory } from '@bandos/shared'
import { CATEGORY_STYLES } from '../../../../../constants/mindmapConfig'
import { StyledButtonWrapper, ButtonText } from '../../styles/MindMapHeaderStyles'
import { MobileTooltip } from './MobileTooltip'
import { TYPOGRAPHY, FONT_WEIGHT } from '../../../../../styles/theme'

const NODE_OPTIONS = [
  {
    category: 'venture' as NodeCategory,
    label: 'Venture',
    icon: Building2,
    description: 'Define your project',
  },
  {
    category: 'need' as NodeCategory,
    label: 'Unmet need',
    icon: Compass,
    description: 'Define unmet needs',
  },
  {
    category: 'idea' as NodeCategory,
    label: 'Idea',
    icon: Lightbulb,
    description: 'Add an idea or vision',
  },
] as const

// --- Styled components ---

const PopoverContent = styled.div`
  width: 280px;
  padding: 8px;
  background: var(--color-bg-elevated);
`

const Header = styled.div`
  padding: 8px 12px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid var(--color-border);
  margin-bottom: 4px;
`

const Option = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease;
  text-align: left;

  &:hover {
    background: var(--color-bg-hover);
  }
`

const IconCircle = styled.div<{ $iconColor: string }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--color-bg-card);
  border: 2px solid ${(props) => props.$iconColor};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const OptionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const OptionLabel = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  color: var(--color-text-primary);
`

const OptionDescription = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  line-height: 1.3;
`

// --- Component ---

interface AddNodePopoverProps {
  onAddNode?: (category: NodeCategory) => void
}

export const AddNodePopover: React.FC<AddNodePopoverProps> = ({ onAddNode }) => {
  const [open, setOpen] = useState(false)

  const handleClick = (category: NodeCategory): void => {
    onAddNode?.(category)
    setOpen(false)
  }

  const content = (
    <PopoverContent>
      <Header>Select node type</Header>
      {NODE_OPTIONS.map((option) => {
        const style = CATEGORY_STYLES[option.category]
        return (
          <Option key={option.category} onClick={() => handleClick(option.category)}>
            <IconCircle $iconColor={style.iconColor}>
              <option.icon size={16} color={style.iconColor} />
            </IconCircle>
            <OptionContent>
              <OptionLabel>{option.label}</OptionLabel>
              <OptionDescription>{option.description}</OptionDescription>
            </OptionContent>
          </Option>
        )
      })}
    </PopoverContent>
  )

  return (
    <MobileTooltip title="Add node">
      <Popover
        content={content}
        trigger="click"
        placement="bottomLeft"
        open={open}
        onOpenChange={setOpen}
        styles={{ body: { background: 'var(--color-bg-elevated)', padding: 0 } }}
      >
        <StyledButtonWrapper>
          <Button type="default" icon={<Plus size={16} />}>
            <ButtonText>Add node</ButtonText>
          </Button>
        </StyledButtonWrapper>
      </Popover>
    </MobileTooltip>
  )
}
